import type { SDKState } from "@/state/sdk-state";
import type { ApiService } from "../api/api";
import * as z from "zod/v4-mini";
import { joinURL } from "@/utils/url";
import { AuthIframeManager } from "./auth-iframe-manager";
import { AsyncFlowMessageType } from "./auth-iframe-broker";
import { Logger } from "@/utils/logger";
import { GqlAuthChannelType } from "@/state/sdk-auth-state";

const InitiateAuthenticationOutputSchema = z.object({
  code: z.string(),
  state: z.string(),
  success: z.boolean(),
});

const UserPublicSchema = z.object({
  id: z.string(),
  email: z.email(),
  username: z.string(),
});

const ValidatedUserIdentifierSchema = z.object({
  type: z.string(),
  value: z.string(),
  confirmation_token: z.string(),
});

const ValidateAuthenticationOutputSchema = z.object({
  success: z.boolean(),
  data: z.nullable(
    z.object({
      state: z.string(),
      user: z.nullable(UserPublicSchema),
      auth_channel: z.enum(GqlAuthChannelType),
      validated_identifiers: z.nullable(z.array(ValidatedUserIdentifierSchema)),
    }),
  ),
});

export class AuthService {
  #state: SDKState;
  #api: ApiService;
  #authIframeManager: AuthIframeManager;
  #logger: Logger;

  constructor(state: SDKState, api: ApiService, logger: Logger) {
    this.#state = state;
    this.#api = api;
    this.#logger = logger.extend(this.constructor.name);
    this.#authIframeManager = new AuthIframeManager(state, this.#logger);
  }

  init() {
    this.#authIframeManager.init();
    this.#authIframeManager.broker.authObservable.subscribe(
      async ({ data }) => {
        if (data.type === AsyncFlowMessageType.ValidateAuthenticationSuccess) {
          await this.validateAuthentication(
            data.code,
            data.state,
            data.redirect_uri,
          );
          // Detach the authentication iframe
          await this.#authIframeManager.detachSilentIframe();
        }
      },
    );
  }

  detach() {
    this.#state.authState.isAuthenticating.value = false;
    this.#authIframeManager.detach();
  }

  async initiateSilentAuthentication() {
    if (this.#state.authState.isAuthenticated) {
      return;
    }

    this.#state.authState.isAuthenticating.value = true;
    const data = await this.#api.POST(`/auth/silent-initiate`, {
      redirectUri: joinURL(this.#state.options.appOrigin, `auth/callback`),
    });

    const parsed = InitiateAuthenticationOutputSchema.safeParse(data);

    if (!parsed.success) {
      this.#logger.error(
        "Failed to parse initiate authentication response",
        parsed.error.message,
      );
      return;
    }

    this.#logger.info("Initiating silent authentication");

    await this.#authIframeManager.createSilentIframe(parsed.data.code);
  }

  async validateAuthentication(
    code: string,
    state: string,
    redirectUri: string,
  ) {
    const data = await this.#api.POST(`/auth/silent-validation`, {
      code,
      state,
      redirectUri,
    });

    const parsed = ValidateAuthenticationOutputSchema.safeParse(data);

    if (!parsed.success) {
      this.#logger.error(
        "Failed to parse validate authentication response",
        parsed.error.message,
      );
      return;
    }

    this.#logger.info("Validated authentication");

    if (parsed.data.data) {
      this.#state.authState.value.set({
        authToken: parsed.data.data.state,
        user: parsed.data.data.user ?? null,
        validatedIdentifiers: parsed.data.data.validated_identifiers ?? [],
        authChannelType: parsed.data.data.auth_channel,
      });
    } else {
      this.#state.authState.value.remove();
    }
    this.#state.authState.isAuthenticating.value = false;
  }
}
